import { Component, EventEmitter, OnInit, ViewChild, Output, Input } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NotificationService, PagedSearch } from 'src/app/shared';
import { SelectItem } from 'primeng/api';
import { BillingCycleName, PledgeDto } from '../../shared/pledge/dto/pledge.dto';
import { PledgeLevelSearchDto } from '../../shared/pledge/dto/pledge-level-search.dto';
import { BillingCycle } from '../../shared/billing/billing-cycle.model';
import { BillingService } from '../../shared/billing/billing.service';
import { PledgeLevelDto } from '../../shared/pledge/dto/pledge-level.dto';
import { PledgeLevelService } from '../../shared/pledge/pledge-level.service';
import { RevenueItem } from '../../shared/revenue-items/revenue-item.model';
import { RevenueItemService } from '../../shared/revenue-items/revenue-item.service';
import { PledgeService } from '../../shared/pledge/pledge.service';
import { PledgeCampaignDto } from '../../shared/pledge-campaign/dto/pledge-campaign.dto';
import { ProfileMin } from 'src/app/profiles/shared/profile-min.model';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { Billing } from '../../shared/billing/billing.model';
import { UserService } from 'src/app/users/shared/user.service';

@Component({
  selector: 'wl-pledge-add-dialog',
  templateUrl: './pledge-add-dialog.component.html',
  styleUrls: ['./pledge-add-dialog.component.less'],
    standalone: false
})
export class PledgeAddDialogComponent implements OnInit {
  @ViewChild(NgForm) private _form: NgForm;
  @Output() pledgeSaved = new EventEmitter<PledgeDto>();
  @Input() profile: ProfileMin;
  @Input() pledgeCampaign: PledgeCampaignDto;

  show = false;
  isSaving = false;
  autoCreateBill = true;

  pledge = {} as PledgeDto;
  pledgeLevels: SelectItem[] = [];
  revItems: SelectItem[] = [];
  billCycles: SelectItem[] = [];
  activeUsers: SelectItem[] = [];

  pledgeLevelSearch = new PagedSearch() as PledgeLevelSearchDto;

  constructor(
    private _revenueItemService: RevenueItemService,
    private _pledgeLevelService: PledgeLevelService,
    private _billingService: BillingService,
    private _notificationService: NotificationService,
    private _pledgeService: PledgeService,
    private _navigationService: NavigationService,
    private _userService: UserService
  ) {}

  ngOnInit() {
    this.getPledgeLevels();
    this.getBillingCycles();
    this.getRevenueItems();
    this.getActiveUsers();
  }

  public open() {
    this.show = true;
    this.pledge.StartDate = new Date();
    this.pledge.EndDate = new Date();
  }

  save() {
    if (this.profile.ProfileId > 0 && this.pledgeCampaign.PledgeCampaignId > 0) {
      this.isSaving = true;
      this.pledge.PledgeCampaignId = this.pledgeCampaign.PledgeCampaignId;
      this.pledge.ProfileId = this.profile.ProfileId;
      this._pledgeService
        .createPledge(this.pledge)
        .then((pledgeId: number) => {
          this.pledgeSaved.emit(this.pledge);
          this._notificationService.translateSuccess('common.saved-successfully');

          if (this.autoCreateBill) {
            this.pledge.PledgeId = pledgeId;
            this.createPledgeBilling(this.pledge);
          }

          this.close();
          this._navigationService.navigateToPledgeEdit(pledgeId);
        })
        .catch((error) => {
          this._notificationService.httpError(error);
        })
        .finally(() => {
          this.isSaving = false;
        });
    }
  }

  close() {
    this.show = false;
    this.pledge = {} as PledgeDto;
  }

  private getPledgeLevels() {
    this._pledgeLevelService
      .searchPledgeLevels(this.pledgeLevelSearch)
      .then((data) => {
        if (data.TotalCount > 0) {
          this.pledgeLevels = data.Result.map<SelectItem>((x: PledgeLevelDto) => {
            return { label: x.PledgeLevel1, value: x.PledgeLevel1 };
          });
        }
      })
      .catch((err) => {
        this._notificationService.httpError(err);
      });
  }

  private getRevenueItems() {
    this._revenueItemService
      .getAllRevenueItems()
      .then((data) => {
        if (data.length > 0) {
          this.revItems = data.map<SelectItem>((x: RevenueItem) => {
            return { label: x.ItemName, value: x.ItemNum };
          });
          this.revItems.sort((a, b) => a.label.localeCompare(b.label));
        }
      })
      .catch((err) => {
        this._notificationService.httpError(err);
      });
  }

  private getBillingCycles() {
    this._billingService
      .getBillingCycles()
      .then((data) => {
        if (data.length > 0) {
          this.billCycles = data.map<SelectItem>((x: BillingCycle) => {
            return { label: x.Name, value: x.Name };
          });
        }
      })
      .catch((err) => {
        this._notificationService.httpError(err);
      });
  }

  private getActiveUsers() {
    this._userService.getActiveUsers().then((data) => {
      if (data.length > 0) {
        this.activeUsers = data.map<SelectItem>((x) => {
          return { label: x.UserName, value: x.UserNum };
        });
        this.activeUsers.sort((a, b) => a.label.localeCompare(b.label));
      }
    });
  }

  private createPledgeBilling(pledge: PledgeDto) {
    let billing = new Billing();
    billing.AmountPerCycle = this.calculateAmountPerCycle(pledge);
    billing.AnchorDay = pledge.StartDate.getDate();
    billing.AnchorMonth = pledge.StartDate.getMonth() + 1;
    billing.StartingYear = pledge.StartDate.getFullYear();
    billing.BillingCycle = pledge.BillingCycle;
    billing.BillingExpireDate = pledge.EndDate;
    billing.BillingExpires = true;
    billing.ItemNum = pledge.ItemNum;
    billing.PledgeID = pledge.PledgeId;
    billing.ProfileId = pledge.ProfileId;
    billing.Quantity = 1;
    billing.RevenueItem = this.revItems.find((ri) => ri.value === pledge.ItemNum).label;
    billing.UnitPrice = this.calculateAmountPerCycle(pledge);
    billing.YearlyAmount = this.calculateYearlyAmount(pledge);

    this._billingService.createBilling(billing).catch((err) => {
      this._notificationService.httpError(err);
    });
  }

  private calculateAmountPerCycle(pledge: PledgeDto) {
    let numMonths = pledge.PledgeYears * 12;
    let monthlyPayment = pledge.PledgeTotal / numMonths;
    let amountPerCycle: number;
    switch (pledge.BillingCycle) {
      case BillingCycleName.EveryMonth:
        amountPerCycle = monthlyPayment;
        break;
      case BillingCycleName.Every3Months:
        amountPerCycle = monthlyPayment * 3;
        break;
      case BillingCycleName.Every6Months:
        amountPerCycle = monthlyPayment * 6;
        break;
      case BillingCycleName.Every12Months:
        amountPerCycle = monthlyPayment * 12;
        break;
    }
    return Number(amountPerCycle.toFixed(2));
  }

  private calculateYearlyAmount(pledge: PledgeDto) {
    let amountPerCycle: number = this.calculateAmountPerCycle(pledge);
    let yearlyAmount: number;
    switch (pledge.BillingCycle) {
      case BillingCycleName.EveryMonth:
        yearlyAmount = amountPerCycle * 12;
        break;
      case BillingCycleName.Every3Months:
        yearlyAmount = amountPerCycle * 4;
        break;
      case BillingCycleName.Every6Months:
        yearlyAmount = amountPerCycle * 2;
        break;
      case BillingCycleName.Every12Months:
        yearlyAmount = amountPerCycle;
        break;
    }
    return Number(yearlyAmount.toFixed(2));
  }
}
